import { Skeleton } from '@vizit/atoms';
import { cx } from '@vizit/theme';

interface SkeletonSection {
  id: string;
  kind: 'rows' | 'score';
  /** Width of the title-bar skeleton — sized to roughly match the real section title. */
  titleWidth: string;
  /** Width of the description-bar skeleton — sized to roughly match the real description text. */
  descWidth: string;
}

const SKELETON_SECTIONS: readonly SkeletonSection[] = [
  { id: 'four_ws', kind: 'rows', titleWidth: 'w-[220px]', descWidth: 'w-[280px]' },
  { id: 'image_quality', kind: 'rows', titleWidth: 'w-[120px]', descWidth: 'w-[320px]' },
  { id: 'composition', kind: 'score', titleWidth: 'w-[210px]', descWidth: 'w-[360px]' },
  { id: 'layout_staging', kind: 'score', titleWidth: 'w-[140px]', descWidth: 'w-[270px]' },
];

interface HeroScoringPanelLoadingProps {
  /** Optional class merged onto the panel container. */
  className?: string;
}

/**
 * Loading skeleton for {@link HeroScoringPanel}. Render while the underlying scores are being computed (image fetch
 * in flight, or BE returned `hero_gs1_components: null` because ISS hasn't scored yet), then swap to the real panel
 * once data arrives. Mirrors the panel's container, section count, and approximate text widths so the transition
 * to the real panel is visually minimal.
 *
 * @returns The skeleton placeholder.
 */
function HeroScoringPanelLoading({ className }: HeroScoringPanelLoadingProps) {
  return (
    <output className={cx('flex flex-col gap-8 bg-primary px-8 py-10', className)} aria-label="Hero scoring loading">
      <div className="flex flex-col gap-10 divide-y divide-gray-utility-200 [&>*:not(:first-child)]:pt-10">
        {SKELETON_SECTIONS.map((section) =>
          section.kind === 'rows' ? (
            <div key={section.id} className="flex items-start gap-3 pr-6">
              {/* Chevron placeholder — small rectangle to evoke the collapsible-trigger icon. */}
              <Skeleton variant="square" className="mt-[7px] h-[8px] w-[12px] shrink-0" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton variant="square" className={cx('h-[20px]', section.titleWidth)} />
                <Skeleton variant="square" className={cx('h-[14px]', section.descWidth)} />
              </div>
              {/* Status badge placeholder — rectangle matching StatusBadge's px-4 py-1 text-sm footprint. */}
              <Skeleton variant="square" className="h-[22px] w-[58px] shrink-0" />
            </div>
          ) : (
            <div key={section.id} className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <Skeleton variant="square" className={cx('h-[20px]', section.titleWidth)} />
                  <Skeleton variant="square" className={cx('h-[14px]', section.descWidth)} />
                </div>
                <Skeleton variant="square" className="h-[22px] w-[58px] shrink-0" />
              </div>
              {/* Score-bar placeholder — full-width 2px bar mirroring HeroScoringScoreSection's meter. */}
              <Skeleton variant="square" className="h-2 w-full" />
            </div>
          ),
        )}
      </div>
    </output>
  );
}

export { HeroScoringPanelLoading };
export type { HeroScoringPanelLoadingProps };
